import { useAuthenticateUMG } from "../../../hooks/authHooks/useAuthenticateUMG";
import UiContent from "../../../UiContent";
import { AppBarHeader } from "../AppBarHeader/AppBarHeader";
import { AppScreenContent } from "../AppScreenContent/AppScreenContent";
import useBodyDarkMode from "./hooks/useBodyDarkMode";
import useErrorToasts from "./hooks/useErrorToasts";
import useIdentifyUser from "./hooks/useIdentifyUser";
import { memo, useEffect, useMemo } from "react";
import { atom, useAtomValue, useSetAtom } from "jotai";
import useLoadUser from "./hooks/useLoadUser";
import useSyncProfileState from "./hooks/useSyncProfileState";
import { useUpdateUserOnLoad } from "./hooks/useUpdateUserOnLoad";
import useUserFavorites from "./hooks/useUserFavorites";
import useUserLocation from "./hooks/useUserLocation";
import useUTM from "./hooks/useUTM";
import useOnlineHeartbeat from "./hooks/useOnlineHeartbeat";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import usePowerUserControls from "../../../hooks/powerUserControlHooks/usePowerUserControls";

// Each component in this file encompasses a hook that needs to be memoized to prevent re-rendering on every state change
// While there may be a way to combined these memoized components into a single component, it's easier to keep them separate for now
const IdentifyUser = () => {
  useIdentifyUser();
  return null;
};

const MemoizedIdentifyUser = memo(IdentifyUser);

const BodyDarkMode = () => {
  useBodyDarkMode();
  return null;
};

const MemoizedBodyDarkMode = memo(BodyDarkMode);

const AuthenticateUMG = () => {
  useAuthenticateUMG();
  return null;
};

const MemoizedAuthenticateUMG = memo(AuthenticateUMG);

const ErrorToasts = () => {
  useErrorToasts();
  return null;
};

const MemoizedErrorToasts = memo(ErrorToasts);

const UserLocation = () => {
  useUserLocation();
  return null;
};

const MemoizedUserLocation = memo(UserLocation);

const UpdateUserOnLoad = () => {
  useUpdateUserOnLoad();
  return null;
};

const MemoizeUpdatedUserOnLoad = memo(UpdateUserOnLoad);

const UTM = () => {
  useUTM();
  return null;
};

const MemoizedUTM = memo(UTM);

const UserFavorites = () => {
  useUserFavorites();
  return null;
};

const MemoizedUserFavorites = memo(UserFavorites);

const SyncProfileState = () => {
  useSyncProfileState();
  return null;
};

const MemoizedSyncProfileState = memo(SyncProfileState);

// This atom is used to prevent the page from flashing when the app state changes
const isInitialLoadAtom = atom(true);

// By setting the atom in this component only when the value of useLoadUser changes, we prevent unnecessary re-renders whenever the useLoadUser hook recalculates
const LoadUser = () => {
  const setIsInitialLoad = useSetAtom(isInitialLoadAtom);
  const hideMainContent = useLoadUser();
  useEffect(() => {
    setIsInitialLoad(hideMainContent);
  }, [setIsInitialLoad, hideMainContent]);
  return null;
};

const MemoizedLoadUser = memo(LoadUser);

const PowerUserControls = () => {
  usePowerUserControls();
  return null;
};

const MemoizedPowerUserControls = memo(PowerUserControls);

const OnlineHeartbeat = () => {
  useOnlineHeartbeat();
  return null;
};

const MemoizedOnlineHeartbeat = memo(OnlineHeartbeat);

// App State itself is also memoized to prevent unnecessary re-renders. This prevents the page from flashing when the app state changes
const AppState = () => {
  const hideMainContent = useAtomValue(isInitialLoadAtom);
  const pageContent = useMemo(() => {
    return (
      <>
        <AppBarHeader isEmptyHeader={hideMainContent} />
        {hideMainContent ? (
          <AppScreenContent isLoading={true} />
        ) : (
          <UiContent />
        )}
      </>
    );
  }, [hideMainContent]);
  return (
    <ErrorBoundary>
      <MemoizedSyncProfileState />
      <MemoizedIdentifyUser />
      <MemoizedErrorToasts />
      <MemoizedUTM />
      <MemoizedUserLocation />
      <MemoizeUpdatedUserOnLoad />
      <MemoizedBodyDarkMode />
      <MemoizedUserFavorites />
      <MemoizedLoadUser />
      <MemoizedPowerUserControls />
      <MemoizedAuthenticateUMG />
      <MemoizedOnlineHeartbeat />
      {pageContent}
    </ErrorBoundary>
  );
};

export default memo(AppState);
